/**
 * Field screening kept on this device so a reload, a swipe-away or a closed
 * tab never loses a screening in progress (rule 8, "refresh never loses
 * work"; refresh audit P1). One screening per device, in localStorage.
 *
 * Kept: step, facility, every captured motor (with its uploaded photo keys
 * and AI notes), the motor being entered, the customer email typed on the
 * report step, and a live capture's id + start time (so polling can resume).
 * Never kept: the access code, the report download link (a signed URL),
 * photo files / previews (only their uploaded S3 keys), or anything
 * secret-looking (noSecrets.js).
 */
import { stripSecrets } from './noSecrets';

export const SCREENING_DRAFT_KEY = 'byrdsense.screeningDraft.v1';
/** The bench-jobs active-capture registration expires after 30 min. */
export const CAPTURE_TTL_MS = 30 * 60 * 1000;

export function loadScreeningDraft() {
  try {
    const d = JSON.parse(window.localStorage.getItem(SCREENING_DRAFT_KEY) || 'null');
    if (!d || typeof d !== 'object' || !d.facility || !Array.isArray(d.motors)) return null;
    return { ...d, step: Math.min(Math.max(Number(d.step) || 1, 1), 3) };
  } catch {
    return null;
  }
}

export function saveScreeningDraft(draft) {
  try {
    window.localStorage.setItem(
      SCREENING_DRAFT_KEY,
      JSON.stringify(stripSecrets({ ...draft, savedAt: new Date().toISOString() })),
    );
    return true;
  } catch {
    return false;
  }
}

export function clearScreeningDraft() {
  try {
    window.localStorage.removeItem(SCREENING_DRAFT_KEY);
  } catch {
    /* nothing stored */
  }
}

/** True when the screening has anything worth keeping. */
export function screeningHasContent({ facility, motors, currentMotor }) {
  const f = facility || {};
  const m = currentMotor || {};
  return Boolean(
    f.company || f.location || f.contact || (motors && motors.length) ||
    Object.entries(m).some(([, v]) => v !== '' && v != null),
  );
}

/** A restored capture can resume only while its registration is alive. */
export function captureStillLive(capture, now = Date.now()) {
  if (!capture || !capture.captureId || !capture.captureStart) return false;
  return now - Date.parse(capture.captureStart) < CAPTURE_TTL_MS;
}
